import { useCallback } from "react";
import { create } from "zustand";
import { development, platform, portalServer } from "./config";
import { clearData } from "./db";
import { usePortalApi } from "./db/portal-api";
import { LicenseData, Modul, moduleOptions, useLicense, useSetLicense } from "./license";
import { logger } from "./log";
import { managerPromise } from "./missions/connect";
import { resetPage } from "./staticFiles";
import { ErrorLike } from "./types";
import { useSettingsStore } from "./data/settings/store";

const log = logger("auth");

// FIXME implement automatic check for license expiry and renewal.
// how often to check whether the license has expired
// const licenseExpiryCheckInterval = 60 * 60 * 1000;

type AuthErrorStore = {
  error?: ErrorLike;
  setError: (error?: ErrorLike) => void;
};

const useAuthErrorStore = create<AuthErrorStore>((set) => ({
  setError: (error) => set({ error }),
}));

export function useAuthError() {
  return useAuthErrorStore((state) => state.error);
}

export function useSignIn(): (
  licenseKey: string,
  pin?: string,
  previousLoadCount?: number,
) => Promise<void> {
  const setAuthError = useAuthErrorStore((state) => state.setError);
  const setLicense = useSetLicense();
  const signOut = useSignOut();
  const { useNewApi, baseUrl } = usePortalApi();

  return useCallback(
    async (licenseKey, pin, previousLoadCount = 0) => {
      try {
        setAuthError(undefined);

        if (useNewApi && !baseUrl) {
          throw new Error("Kein Portal-Server konfiguriert.");
        }

        const license = useNewApi
          ? await signInNew(baseUrl!, licenseKey, pin, previousLoadCount)
          : await signInLegacy(licenseKey, previousLoadCount);

        if (license.gueltigkeit && license.gueltigkeit <= Date.now()) {
          log.info("license has expired, signing out:", license.gueltigkeit);
          await signOut(
            new Error(
              `Ihre Lizenz ist am ${new Date(
                license.gueltigkeit,
              ).toLocaleString()} abgelaufen. Bitte wenden Sie sich an den rescueTABLET-Support.`,
            ),
          );
        } else {
          await setLicense(license);
        }
      } catch (error: any) {
        log.error("Authentication failed:", error);
        setAuthError(error);
      }
    },
    [setAuthError, setLicense, signOut, useNewApi, baseUrl],
  );
}

async function signInLegacy(
  licenseKey: string,
  previousLoadCount: number,
): Promise<LicenseData> {
  const response = await fetch(
    `${portalServer}/get-device.php?DEVICE=${encodeURIComponent(licenseKey)}`,
  );
  if (!response.ok)
    throw new Error(`Fehler: ${response.status} ${response.statusText}`);
  const data = await response.json();
  if (data.success === false) throw new Error("Ungültiger Lizenzschlüssel");

  return {
    ...data,
    missionApiKey: data.missionApiKey || undefined,
    gueltigkeit: data.gueltigkeit
      ? new Date(data.gueltigkeit).getTime()
      : undefined,
    homebase: data.homebase
      ? { latitude: data.homebase.lat, longitude: data.homebase.lng }
      : undefined,
    loadCount: previousLoadCount + 1,
  };
}

async function signInNew(
  baseUrl: string,
  licenseKey: string,
  pin: string | undefined,
  previousLoadCount: number,
): Promise<LicenseData> {
  const body: Record<string, string> = { licenseKey };
  if (pin) body.pin = pin;

  const response = await fetch(`${baseUrl}/api/device/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const message = await response
      .json()
      .then((body) => body?.message as string | undefined)
      .catch(() => undefined);
    throw new Error(message || `Fehler: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return mapNewApiLicense(data, previousLoadCount);
}

const warnkarteByState: Record<string, string> = {
  "Baden-Württemberg": "baw",
  "Bayern": "bay",
  "Berlin": "bbb",
  "Brandenburg": "bbb",
  "Hessen": "hes",
  "Mecklenburg-Vorpommern": "mvp",
  "Niedersachsen": "nib",
  "Bremen": "nib",
  "Nordrhein-Westfalen": "nrw",
  "Rheinland-Pfalz": "rps",
  "Saarland": "rps",
  "Sachsen": "sac",
  "Sachsen-Anhalt": "saa",
  "Schleswig-Holstein": "shh",
  "Hamburg": "shh",
  "Thüringen": "thu",
};

function mapNewApiLicense(data: any, previousLoadCount: number): LicenseData {
  const { session, license } = data ?? {};

  if (!session || !license) {
    throw new Error("Ungültige Antwort vom Portal-Server.");
  }

  const module = (license.modules ?? [])
    .map((m: any) => m.value as string)
    .filter((v: string): v is Modul =>
      (moduleOptions as ReadonlyArray<string>).includes(v),
    );

  const features = (license.features ?? []).map((f: any) => f.value as string);

  return {
    // id holds licenseKey so useRenewLicense can re-authenticate
    id: license.licenseKey,
    name: license.name,
    positionName: license.shortName,
    einheit: { id: license.organizationId, name: license.name },
    homebase:
      license.lat != null && license.lng != null
        ? { latitude: license.lat, longitude: license.lng }
        : undefined,

    settingsScheme: license.settingsScheme,
    portalKey: session.portalKey,
    alarmKey: "",
    missionApiKey: license.missionApiKey || undefined,
    module,
    features,
    warnkarte: license?.state && warnkarteByState[license.state]
      ? [warnkarteByState[license.state]]
      : [],
    gueltigkeit: license.validUntil
      ? new Date(license.validUntil).getTime()
      : undefined,
    positionSenden: license.locationSend ?? false,
    positionSehen: license.locationView ?? false,
    freeversion: license.licenseType === "FREE",
    demo: license.licenseType === "DEMO",
    offlineArea: license.offlineArea ?? undefined,
    loadCount: previousLoadCount + 1,
  };
}

export function useSignOut(): (reason?: ErrorLike) => Promise<void> {
  const setAuthError = useAuthErrorStore((state) => state.setError);
  const setLicense = useSetLicense();

  return useCallback(
    async (reason) => {
      await managerPromise
        .then((manager) => manager.deauthorize())
        .catch((error) => log.warn("Failed to deauthorize Firebase:", error));

      await clearData().catch((error) =>
        log.warn("Failed to clear local data on sign-out:", error),
      );

      if (development || reason || platform !== "web") {
        await setLicense();
        setAuthError(reason);
        window.location.href = "/";
      } else {
        window.location.href = resetPage;
      }
    },
    [setLicense, setAuthError],
  );
}

export function useRenewLicense(): () => Promise<void> {
  const license = useLicense();
  const signIn = useSignIn();

  return useCallback(async () => {
    if (license) await signIn(license.id, undefined, license.loadCount);
  }, [license, signIn]);
}

export function useAuthenticated(): LicenseData {
  const license = useLicense();
  if (!license) throw new Error("Unauthenticated");
  return license;
}

/**
 * The active settingsScheme, normally taken from the license, but can be
 * temporarily overridden via the settings dialog on the login screen.
 */
export function useSettingsScheme(): string | undefined {
  const { settingsScheme } = useAuthenticated();
  const override = useSettingsStore(
    (state) => state.settings.settingsSchemeOverride,
  );
  return override ?? settingsScheme;
}

export function useModulEnabled(modul: Modul): boolean {
  const { module } = useAuthenticated();
  return module.includes(modul);
}

/** Throw if the designated module is not available to the current user. */
export function useRequiredModul(modul: Modul): void {
  const enabled = useModulEnabled(modul);
  if (!enabled) throw new Error(`Modul ${modul} ist nicht aktiviert.`);
}

export function useFeatureEnabled(feature: string): boolean {
  const { features } = useAuthenticated();
  if (!features) return false;
  return features.includes(feature);
}
